import { Table, Paper, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { FC, useMemo } from "react";
import ProjetosPropostasComponentesDom, { ProjetosPropostasCupomDom } from "../PropostaSimplificada/types";
import { DecimalUtil } from "src/framework/commons/DecimalUtils";
import { DateUtil } from "src/framework/commons/DateUtils";
import { ORDEM_GRUPO_CATEGORIAS } from "src/components/projetos/constants";
import { TGrupoCategoria } from "src/components/componentes/types";
import { TCategoriasComponentes } from "src/components/projetos/types";

interface InvestimentoProps {
    data: any;
    componenteFiname: any;
}

const Investimento: FC<InvestimentoProps> = ({
    data,
    componenteFiname,
}) => {

    const textCupom: string = useMemo(() => {
        let text = "";
        if (data?.cupom && !!data?.cupom?.length) {
            data?.cupom?.forEach((cupom: ProjetosPropostasCupomDom) => {
                if (cupom.cupom_desconto && cupom.data_expiracao_cupom_desconto) {
                    text += `${cupom.cupom_desconto}-${DateUtil.date(cupom.data_expiracao_cupom_desconto.toString())}`;
                }
            });
        }
        return text;
    }, [data?.cupom]);

    const totalDiscount: number = useMemo(() => {
        let desconto = 0;
        if (data?.cupom && !!data?.cupom?.length) {
            data?.cupom?.forEach((cupom: ProjetosPropostasCupomDom) => {
                if (cupom.valor_desconto && typeof cupom.valor_desconto === "number") {
                    desconto += cupom.valor_desconto;
                }
            });
        }
        return desconto;
    }, [data?.cupom]);

    const valorProjeto: number = useMemo(() => {
        return (data?.projeto?.valor_projeto || 0) - totalDiscount || 0;
    }, [data?.projeto?.valor_projeto, totalDiscount]);

    let diasExpiracao = "";
    
    if (data?.parametros && data?.parametros.length>0){
        const paramExpiracao = data.parametros.filter((obj: any) => obj.id === "PROJETOS_DIAS_EXPIRACAO")
        if(paramExpiracao){
            const dias = paramExpiracao[0].valor ? paramExpiracao[0].valor === "0" ? 1 : paramExpiracao[0].valor : 1;
            diasExpiracao = Number(dias) === 1 ? `${dias} dia` : `${dias} dias`;
        }
    }

    const categoriasComponentes: TCategoriasComponentes[] = useMemo(()=>{
        if(data?.componentesCategorias && !!data?.componentesCategorias?.length){
            return data?.componentesCategorias || [];
        }
        return [];
    }, [data?.componentesCategorias])

    const ordenarComponentesPorCategoria = (
        items: ProjetosPropostasComponentesDom[],
    ) => {
        return items?.map(item => {
            const categoria = categoriasComponentes?.find((i: TCategoriasComponentes) => i.id === item?.categoria_componentes_id);
            return {
                ...item,
                nome_categoria: categoria?.nome || "",
                grupo_categoria: categoria?.grupo || "",
                ordem_listagem: categoria?.ordem_listagem || item?.categoria_componentes_id,
            };
        })?.sort((a, b) => {
                const pesoGrupoA = ORDEM_GRUPO_CATEGORIAS[a.grupo_categoria as TGrupoCategoria] || a?.categoria_componentes_id;
                const pesoGrupoB = ORDEM_GRUPO_CATEGORIAS[b.grupo_categoria as TGrupoCategoria] || b?.categoria_componentes_id;
                if (pesoGrupoA !== pesoGrupoB) {
                    return pesoGrupoA - pesoGrupoB;
                }
                return a.ordem_listagem - b.ordem_listagem;
            });
    };

    const calculaRateio  = useMemo(() => {
        if (data?.projeto && data?.componentes && (totalDiscount > 0 || Number(data?.projeto?.valor_desconto) > 0)) {
            const componentes = [...data?.componentes];
            const valorTotalRateio = data?.projeto?.valor;
            const valorDesconto = Number(data?.projeto?.valor_desconto) + Number(totalDiscount);
            const percentualDesconto =  DecimalUtil.toFixed(valorDesconto / valorTotalRateio, 2);

            const componentesResult = [];

            for (const idx in componentes) {
                const valorItem = componentes[idx].valor_unitario;
                const descontoItem =  DecimalUtil.toFixed(valorItem * percentualDesconto, 2);
                let novoValor = valorItem - descontoItem;

                // Garantir que o valor não fique zerado ou negativo
                if (novoValor <= 0) novoValor = 0.01;

                const componente = {...componentes[idx]};
                componente.valor_unitario = DecimalUtil.toFixed(novoValor, 2);
                componentesResult.push({...componente});
            };


            const valorTotalEsperado = (data?.projeto?.valor - data?.projeto?.valor_desconto - totalDiscount);
            const somaAtual = Number(componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0).toFixed(2));
            let diferenca = DecimalUtil.toFixed(valorTotalEsperado - somaAtual, 2);

            if (diferenca !== 0) {
                const itemMaiorValorUnico = componentesResult
                ?.sort((a, b) => {
                    if (b.valor_unitario !== a.valor_unitario) return b.valor_unitario - a.valor_unitario;
                    return a.quantidade - b.quantidade;
                })[0];

                if (itemMaiorValorUnico) {
                    const index = componentesResult.findIndex(i => i.id === itemMaiorValorUnico.id);
                    if (index > -1) {
                        const novoValorCorrigido = Number(itemMaiorValorUnico.valor_unitario) + Number(diferenca / itemMaiorValorUnico.quantidade);
                        componentesResult[index].valor_unitario = Number(novoValorCorrigido.toFixed(2));
                    }
                }
            }

            // Normalização para eliminar diferenças por arredondamento
            let totalFinal = Number(componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0).toFixed(2));
            totalFinal = Math.round(totalFinal * 100) / 100;
            const valorEsperadoNormalizado = Math.round(valorTotalEsperado * 100) / 100;
            let diferencaFinal = DecimalUtil.toFixed(valorEsperadoNormalizado - totalFinal, 2);

            if (Math.abs(diferencaFinal) >= 0.01) {
                const itemAjusteFinal = componentesResult
                ?.sort((a, b) => {
                    if (b.valor_unitario !== a.valor_unitario) return b.valor_unitario - a.valor_unitario;
                    return a.quantidade - b.quantidade;
                })[0];

                if (itemAjusteFinal) {
                    const index = componentesResult.findIndex(i => i.id === itemAjusteFinal.id);
                    if (index > -1) {
                        const novoValorFinal = componentesResult[index].valor_unitario + (diferencaFinal / componentesResult[index].quantidade);
                        componentesResult[index].valor_unitario = Number(novoValorFinal.toFixed(2));
                    }
                }

                totalFinal = Math.round(
                    componentesResult.reduce((acc, item) => acc + item.valor_unitario * item.quantidade, 0) * 100
                ) / 100;
            }


            return ordenarComponentesPorCategoria(componentesResult);
        }

        return ordenarComponentesPorCategoria(data?.componentes || []);
    }, [data?.projeto, data?.componentes, totalDiscount]);

    return <>

        <div className="title">Investimento</div>

        <div className="default-text-list"><span>Compreende no orçamento do projeto{componenteFiname ? ` - ${componenteFiname}:` : ":"}</span></div>

        <Table component={Paper} size="small" className="table">
            <TableHead>
                <TableRow>
                    <TableCell className="tableColEquipamentoHead font-intelbras-bold bg-verde-intelbras">Equipamento</TableCell>
                    <TableCell className="tableColMarcaHead font-intelbras-bold bg-verde-intelbras">Marca</TableCell>
                    <TableCell className="tableColQuantidadesHead font-intelbras-bold bg-verde-intelbras">Quant.</TableCell>
                    {data?.valoresUnitarios && (
                        <>
                            <TableCell className="tableColValoresUnitariosHead font-intelbras-bold bg-verde-intelbras">Valor Final</TableCell>
                            <TableCell className="tableColServicoInstalacaoHead font-intelbras-bold bg-verde-intelbras">Total Final</TableCell>
                        </>
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {calculaRateio?.map((comp: any, index: number) => (
                    <TableRow key={index}>
                        <TableCell className={`${(index % 2 === 0) ? 'tableColEquipamento' : 'tableColEquipamentoOdd'}`}>
                            <span className="tableCellEquipamentos">{comp.nome}</span>
                            <br />
                            {comp.descricao && <small>{comp.descricao}</small>}
                        </TableCell>
                        <TableCell className={`${(index % 2 === 0) ? 'tableColMarca' : 'tableColMarcaOdd'}`}>
                            <span className="tableCellMarca">{comp.parceiro || ""}</span>
                        </TableCell>
                        <TableCell className={`${(index % 2 === 0) ? 'tableColQuantidades' : 'tableColQuantidadesOdd'}`}>
                            <span className="tableCellQuantidades">{comp.quantidade}</span>
                        </TableCell>
                        {data?.valoresUnitarios && (
                            <>
                                <TableCell className={`${(index % 2 === 0) ? 'tableColValUnitarios' : 'tableColValUnitariosOdd'}`}>
                                    <span className="tableCellValor">R$ {DecimalUtil.decimal(comp.valor_unitario)}</span>
                                </TableCell>
                                <TableCell className={`${(index % 2 === 0) ? 'tableColValUnitarios' : 'tableColValUnitariosOdd'}`}>
                                    <span className="tableCellValor">R$ {DecimalUtil.decimal(comp.valor_unitario * comp.quantidade)}</span>
                                </TableCell>
                            </>
                        )}
                    </TableRow>
                ))}
                {textCupom && totalDiscount > 0 && (
                    <TableRow>
                        <TableCell className={`${((data?.componentes?.length || 0) % 2 === 0) ? 'tableColCupom' : 'tableColCupomOdd'}`}>
                            Cupom de Desconto ({textCupom})
                        </TableCell>
                        <TableCell colSpan={data?.valoresUnitarios ? 4 : 3} className={`${((data?.componentes?.length || 0) % 2 === 0) ? 'tableColCupomValor' : 'tableColCupomValorOdd'}`}>R$ {DecimalUtil.decimal(totalDiscount)}</TableCell>
                    </TableRow>
                )}
                {(data.valoresUnitarios && data.projeto?.valor_servico) ? (<>
                    <TableCell className={`${((data?.componentes?.length || 0) % 2 !== 0) ? 'tableColCustoTotal' : 'tableColCustoTotalOdd'} text-bold`}>
                        <span className="custoTotalTexto">Serviço de Instalação:</span>
                    </TableCell>
                    <TableCell colSpan={data?.valoresUnitarios ? 4 : 3} className={`${((data?.componentes?.length || 0) % 2 !== 0) ? 'tableColCustoTotalValor' : 'tableColCustoTotalValorOdd'} text-bold`}>
                        R$ {DecimalUtil.decimal(data.projeto?.valor_servico_pis_cofins)}
                    </TableCell>
                </>): null}
                <TableRow>
                    <TableCell className={`${((data?.componentes?.length || 0) % 2 !== 0) ? 'tableColCustoTotal' : 'tableColCustoTotalOdd'} text-bold`}>
                        <span className="custoTotalTexto">Custo total:</span>
                    </TableCell>
                    <TableCell colSpan={data?.valoresUnitarios ? 4 : 3} className={`${((data?.componentes?.length || 0) % 2 !== 0) ? 'tableColCustoTotalValor' : 'tableColCustoTotalValorOdd'} text-bold`}>
                        R$ {DecimalUtil.decimal(valorProjeto)}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
        
        <div style={{ marginTop: "40pt" }}></div>
        <div className="default-text-list default-small-text"><span>* Este é um pré-orçamento e tem validade de {diasExpiracao ? diasExpiracao : "15 (quinze) dias"}, ou enquanto durarem os estoques. A Intelbras pode, constatada a compra e a seu exclusivo critério, encaminhar um sistema com potencial de geração igual ou superior ao especificado acima, caso haja disponibilidade de estoque dos produtos, sem ônus adicional, estando o cliente ciente e de acordo com essas condições.</span></div>
    </>
}

export default Investimento;

